import { useState } from 'react';
import { useLocation } from 'wouter';
import { usePortalStore } from '@/store/portal-store.js';
import { Shield, AlertCircle } from 'lucide-react';
import { customInstance } from '@edison/api-client-react';

export function PortalLoginPage() {
  const [token, setToken] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [canRenew, setCanRenew] = useState(false);
  const [renewNote, setRenewNote] = useState('');
  const [renewSent, setRenewSent] = useState(false);
  const [, navigate] = useLocation();
  const { setToken: storeToken } = usePortalStore();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token.trim()) return;

    setIsLoading(true);
    setError('');
    setCanRenew(false);

    try {
      // Validate the token by fetching the claim overview
      await customInstance<{ data: unknown }>({
        url: '/api/portal/claim',
        method: 'GET',
        headers: { 'X-Access-Token': token.trim() },
      });

      storeToken(token.trim());
      navigate('/portal/ask');
    } catch (err) {
      const e2 = err as { response?: { status?: number; data?: { error?: { code?: string } } } };
      const code = e2?.response?.data?.error?.code;
      if (e2?.response?.status === 401 && (code === 'TOKEN_INACTIVE' || code === 'INVALID_TOKEN')) {
        // Expired or idle — offer a renewal request rather than a dead end.
        setError('This access token has expired or been inactive too long.');
        setCanRenew(true);
      } else {
        setError('Invalid access token. Please check the token and try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const requestRenewal = async () => {
    try {
      await customInstance({
        url: '/api/portal/request-renewal',
        method: 'POST',
        data: { token: token.trim(), note: renewNote.trim() || undefined },
      });
    } catch { /* generic response either way */ }
    setRenewSent(true);
    setCanRenew(false);
  };

  return (
    <div className="dark min-h-screen bg-gray-950 flex items-center justify-center p-4">
      <div className="w-full max-w-sm space-y-6">
        <div className="text-center space-y-2">
          <Shield className="h-10 w-10 text-blue-400 mx-auto" aria-hidden="true" />
          <h1 className="text-xl font-semibold text-white">Edison R&D Audit Portal</h1>
          <p className="text-sm text-gray-400">
            Enter your secure access token to view the R&D evidence package.
          </p>
        </div>

        {error && (
          <div className="rounded-lg border border-red-900/50 bg-red-950/50 p-3 flex items-center gap-2 text-red-400 text-sm">
            <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
            <span>{error}</span>
          </div>
        )}

        {renewSent && (
          <div className="rounded-lg border border-emerald-900/50 bg-emerald-950/50 p-3 text-emerald-400 text-sm">
            Renewal request sent. The claim owner will review it and send you a new access link.
          </div>
        )}

        {canRenew && !renewSent && (
          <div className="rounded-lg border border-gray-700 bg-gray-900 p-3 space-y-2">
            <p className="text-sm text-gray-300">Still need access? Request a renewed token.</p>
            <textarea
              value={renewNote}
              onChange={(e) => setRenewNote(e.target.value)}
              placeholder="Optional note for the claim owner (e.g. review still in progress)"
              rows={2}
              className="w-full rounded-lg border border-gray-700 bg-gray-950 px-3 py-2 text-sm text-white placeholder:text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              type="button"
              onClick={() => void requestRenewal()}
              className="w-full rounded-lg bg-gray-700 py-2 text-sm font-medium text-white hover:bg-gray-600"
            >
              Request renewed access
            </button>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <label htmlFor="portal-token" className="block text-sm font-medium text-gray-300">
              Access token
            </label>
            <input
              id="portal-token"
              type="password"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="Paste your access token here"
              className="w-full rounded-lg border border-gray-700 bg-gray-900 px-3 py-2 text-sm text-white placeholder:text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
              autoComplete="off"
            />
          </div>
          <button
            type="submit"
            disabled={isLoading || !token.trim()}
            className="w-full rounded-lg bg-blue-600 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {isLoading ? 'Verifying…' : 'Access portal'}
          </button>
        </form>

        <p className="text-xs text-gray-600 text-center">
          Access tokens are time-limited and scoped to a specific R&D claim.
          All access is logged for audit purposes.
        </p>
      </div>
    </div>
  );
}
